import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import { showNotification } from '../../App';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { mainTheme } from '../../Theme';
import { retrieveEnquiryOutcomeData, EnquiryOutcomeData, Outcome } from './OutcomeRadio';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'inline-flex',
            border: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            borderRadius: 8,
            overflow: 'hidden',
        },
        segment: {
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            backgroundColor: '#fff',
            borderRight: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            '&:last-child': {
                borderRight: 'none',
            },
        },
        // Kept for "More Follow Up" (explicit request: its current look is good as-is) and as
        // the fallback tone for any option that isn't one of the two colour-coded ones below.
        segmentSelected: {
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
        },
        // Explicit request: "Proceeding" reads as a positive outcome once picked.
        segmentSelectedGreen: {
            backgroundColor: 'var(--rv-accent-green-bg)',
            color: 'var(--rv-accent-green)',
        },
        // Explicit request: "Not Proceeding" gets a light amber/orange once picked.
        segmentSelectedOrange: {
            backgroundColor: 'var(--rv-accent-orange-bg)',
            color: 'var(--rv-accent-orange)',
        },
    }),
);

// Only changes which selected-state colour class is applied - onSegmentClick/value contract
// below is untouched.
const getSelectedToneClassName = (classes: Record<string, string>, label: string): string => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel === 'proceeding') {
        return classes.segmentSelectedGreen;
    }
    if (lowerLabel === 'not proceeding') {
        return classes.segmentSelectedOrange;
    }
    return classes.segmentSelected;
};

interface OutcomeSegmentedControlProps {
    name: string;
    label: string;
    hasNameGuid: boolean;
    canCreateMatter: boolean | undefined;
    value?: string;
    onRadioButtonChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

// Explicit request (reference: Followup.png) - OutcomeRadio's own 3-option list (data/filtering
// unchanged, see OutcomeRadio.tsx's retrieveEnquiryOutcomeData) rendered as a segmented control
// instead of radio buttons. A drop-in visual replacement, same onRadioButtonChange(event,
// checked) contract, event.currentTarget.value carrying the selected option's label exactly as
// the radio version's own handleOnChange already did.
export const OutcomeSegmentedControl: React.FC<OutcomeSegmentedControlProps> = (props) => {
    const classes = useStyles();
    const [options, setOptions] = useState<IAutoCompleteItem[]>([]);

    useEffect(() => {
        retrieveEnquiryOutcomeData(
            false,
            (data: EnquiryOutcomeData) => {
                const outcomeList = data.enquiry.outcomes
                    .filter((source: Outcome) => source.description !== 'New Client' && source.description !== 'New Matter');
                setOptions(outcomeList.map((source: Outcome) => ({ value: source.guidId, label: source.description })));
            },
            // tslint:disable-next-line:no-any
            (reason: any) => showNotification(null, reason, 'error'),
        );
    // tslint:disable-next-line: align
    }, [props.hasNameGuid]);

    const onSegmentClick = (option: IAutoCompleteItem) => {
        if (!props.onRadioButtonChange) {
            return;
        }
        const syntheticEvent = {
            currentTarget: { value: option.label },
        } as React.ChangeEvent<HTMLInputElement>;
        props.onRadioButtonChange(syntheticEvent, true);
    };

    return (
        <div className={classes.root} data-tour="enquiry-outcome">
            {options.map((option) => {
                const isSelected = option.label === props.value;
                return (
                    <ButtonBase
                        key={option.value}
                        className={isSelected ? `${classes.segment} ${getSelectedToneClassName(classes, option.label)}` : classes.segment}
                        onClick={() => onSegmentClick(option)}
                    >
                        {option.label}
                    </ButtonBase>
                );
            })}
        </div>
    );
};
